import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  UploadTaskSnapshot,
} from 'firebase/storage';
import { storage } from '@/backend/lib/firebase';
import type { PolicyKey } from '@/shared/types/policySettings';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_FILE_TYPE = 'application/pdf';

export interface UploadPolicyParams {
  file: File;
  key: PolicyKey;
  onProgress?: (progress: number) => void;
}

export interface UploadPolicyResult {
  url: string;
  path: string;
  size: number;
}

export const uploadPolicyPDF = async (
  params: UploadPolicyParams,
): Promise<UploadPolicyResult> => {
  const { file, key, onProgress } = params;

  if (file.type !== ALLOWED_FILE_TYPE) {
    throw new Error('Only PDF files are accepted');
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error('File must be smaller than 10MB');
  }

  const storagePath = `settings/policies/${key}-${Date.now()}.pdf`;
  const storageRef = ref(storage, storagePath);

  const uploadTask = uploadBytesResumable(storageRef, file);

  return new Promise<UploadPolicyResult>((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot: UploadTaskSnapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress?.(Math.round(progress));
      },
      (error) => {
        switch (error.code) {
          case 'storage/unauthorized':
            reject(new Error('You do not have permission to upload files'));
            break;
          case 'storage/canceled':
            reject(new Error('Upload was cancelled'));
            break;
          case 'storage/unknown':
            if (error.message.includes('network')) {
              reject(new Error('Connection error. Check your internet and retry.'));
            } else {
              reject(new Error('Upload failed. Please try again.'));
            }
            break;
          default:
            reject(new Error('Upload failed. Please try again.'));
        }
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve({ url: downloadURL, path: storagePath, size: file.size });
        } catch {
          reject(new Error('Failed to get download URL. Please try again.'));
        }
      },
    );
  });
};

export const deletePolicyPDF = async (filePath: string): Promise<void> => {
  try {
    const fileRef = ref(storage, filePath);
    await deleteObject(fileRef);
  } catch (error: unknown) {
    if (error instanceof Error) {
      if (error.message.includes('storage/object-not-found')) {
        return;
      }
      throw error;
    }
    throw new Error('Failed to delete file. Please try again.');
  }
};
